import React, {useEffect, useState} from 'react';
import oktoberfest from "../../assets/icon/oktoberfest.gif";
import * as util from "../../utils/util";
import api from "../../utils/api";

import Bottom from "../Bottom";
import {Box, Button, Grid, Typography} from "@mui/material";
import Top from "../Top";
import HistoryLoadState from "../../components/HistoryLoadState";
import Circle from "../../components/CircleProgress";
import BarChart from "../../components/BarChart";
import HistorySliderInput from "../../components/HistorySliderInput";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import {useNavigate} from "react-router-dom";
import { notifyApiError } from "../../utils/apiError";


const params = {
  token: '',
  from: '',
  to: '',
  target: ''
}

const initTargetMoisture = {
  moisture: 0,
  target_moisture: 0
}

const HistoryBurnKcal = () => {
  const navigate = useNavigate();
  const [targetMoisture, setTargetMoisture] = React.useState(initTargetMoisture);
  const [series, setSeries] = React.useState([
    { dataKey: 'moisture', label: '수분섭취량', valueFormatter: (value: number | null) => `${value}mm`, stack: 'total' }
  ]);
  const [dataset, setDataset] = React.useState([{ target: 0, moisture: 0, month: '' }]);
  const [dayValue, setDayValue] = useState(0);
  /**
   * 화면 상단 날짜. 서버 응답이 아니라 dayValue 에서 바로 만든다.
   *
   * 예전에는 빈 문자열로 시작해 조회 성공(result_code === '0') 안에서만 채웠다.
   * 그래서 (1) 응답이 올 때까지 '..' 만 보였고 (2) 조회가 실패하면 영영 '..' 로 남았다.
   * 날짜는 클라이언트가 이미 아는 값이라 서버 응답에 묶어 둘 이유가 없다.
   */
  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const [reloadNonce, setReloadNonce] = React.useState(0);

  useEffect(() => {
    setMoistureData();
  }, [dayValue, reloadNonce]);

  function setMoistureData() {
    setLoading(true);
    setFailed(false);
    // 수분섭취 정보 조회
    params.token = localStorage.getItem('token') || '';
    params.from = util.getDateByCustom('yyyyMMdd', dayValue - 7);
    params.to = util.getDateByCustom('yyyyMMdd', dayValue);
    params.target = util.getDateByCustom('yyyyMMdd', dayValue);

    api.get(`app/api/v1/AP_002_004?token=${params.token}&from=${params.from}&to=${params.to}&target=${params.target}`).then(res => {
      if (res.data.result_code === '0') {
        setTargetMoisture(res.data.result_data.target);
        setSeries(series);
        let tempArr = [];
        for (let item of res.data.result_data.data.moisture_v2) {
          let obj = { target: 0, moisture: 0, month: '' };
          obj.month = item.date;
          obj.moisture = item.moisture;
          tempArr.push(obj);
        }
        setDataset(tempArr.reverse());
      } else {
        // result_code 가 '0' 이 아니면 조회 실패다. 예전에는 아무 처리도 없어서
        // 수치는 0 인 채로, 날짜는 '..' 로 굳고 사용자에게는 아무 안내도 없었다.
        console.warn('[기록] 조회 실패 result_code=', res.data.result_code, res.data.result_message);
        setFailed(true);
      }
    }).catch(function (error) {
      console.log(error);
      setFailed(true);
    }).finally(function () {
      setLoading(false);
    });
  }

  // 수분섭취 저장
  function saveMoisture(value: any) {
    const saveParams = { date: '', moisture: '' };
    saveParams.date = targetDate.slice(0, 4) + '-' + targetDate.slice(4, 6) + '-' + targetDate.slice(6, 8);
    saveParams.moisture = value;

    api.post(`app/api/v1/AP_002_008`, saveParams).then(res => {
      setMoistureData();
    }).catch(function (error) {
      // 저장 실패를 알리지 않으면 사용자는 기록이 남은 줄 안다
      notifyApiError(error, '수분 섭취 기록을 저장하지 못했습니다.');
    });
  }

  // 목표 수분섭취 저장
  function saveTargetMoisture(value: any) {
    const saveParams = { date: '', targetMoisture: '' };
    saveParams.date = targetDate.slice(0, 4) + '-' + targetDate.slice(4, 6) + '-' + targetDate.slice(6, 8);
    saveParams.targetMoisture = value;

    api.post(`app/api/v1/AP_002_011`, saveParams).then(res => {
      setMoistureData();
    }).catch(function (error) {
      // 저장 실패를 알리지 않으면 사용자는 기록이 남은 줄 안다
      notifyApiError(error, '목표 수분 섭취량을 저장하지 못했습니다.');
    });
  }



  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title="수분 기록" />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>
          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 조회 중 / 실패 표시 — 날짜 바로 아래 한 줄 */}
          <HistoryLoadState loading={loading} failed={failed} onRetry={() => setReloadNonce((n) => n + 1)} />

          <Stack direction={'row'} justifyContent={'center'} sx={{ m: '20px 0', p: 1, bgcolor: '#fff', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
            <Grid sx={{
              '& .CircleChart .MuiGauge-root': { width: '130px', height: '130px' },
              '& .CircleChart .circleText': { fontSize: 24, },
              '& .MuiGauge-valueArc': {
                fill: '#33cccc !important',
              },
            }}>
              <Circle progress={{ goal: targetMoisture.target_moisture, value: targetMoisture.moisture }} />
            </Grid>

            <Stack justifyContent={'center'} sx={{ m: 'auto', }}>
              <Avatar alt="" src={oktoberfest} sx={{ m: 'auto', width: '60px', height: '60px', borderRadius: 0 }} />
            </Stack>

            <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
              <Stack direction={'column'} sx={{ ml: 'auto' }}>
                <Typography component="div" sx={{ fontSize: 35, color: '#33cccc', fontWeight: 700 }}>
                  {util.formatComma(targetMoisture.moisture)}
                </Typography>
                <Typography component="span" sx={{ fontSize: 20, color: '#999', mb: 0, textAlign: 'right' }}>
                  {util.formatComma(targetMoisture.target_moisture)}
                </Typography>
              </Stack>

              <Typography component="div" sx={{ fontSize: 18, color: '#777', ml: 1, mb: 2 }}>
                cup
              </Typography>
            </Stack>
          </Stack>

          <Grid container justifyContent="center" alignItems="center" >
            <HistorySliderInput info={{ value: targetMoisture.moisture, title: '오늘의 수분 섭취량을 입력하세요', unit: 'cup', btnTitle: '입력 하기', max: 20 }} handleComplete={saveMoisture} />
            <HistorySliderInput info={{ value: targetMoisture.target_moisture, title: '오늘의 목표 수분 섭취량을 입력하세요', unit: 'cup', btnTitle: '목표 설정', max: 50 }} handleComplete={saveTargetMoisture} />
          </Grid>

          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)' }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack >
                <Typography component="div" sx={{ fontSize: 14, color: '#555', }}>최근 7일</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2  }} >
                <Button  sx={{fontSize:12, color:'#555', }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/MonthHistory', {state: {type: 'moisture'}}) }}>
                월간현황
                </Button>
              </Stack>
            </Stack>
            <Divider />
            <Box sx={{
              mt: -3.5,
              '& .MuiBarElement-root': { fill: '#33cccc' },
              '& .MuiChartsLegend-mark': { fill: '#33cccc' }
            }}>
              <BarChart dataset={dataset}
                chartSetting={chartSet}
                series={series}
              />
            </Box>
          </Box>
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

/*
const EditButton = styled(Button)({
  boxShadow: 'none',
  textTransform: 'none',
  fontSize: 16,
  fontWeight:700,
  padding: '6px 20px',
  lineHeight: 1.5,
  backgroundColor: '#fff',
  border:'1px solid #EF8322',
  borderRadius:'20px',
  color:'#EF8322',
  margin:'10px 0 30px 0',
  width:'47%',
  '&:hover': {
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
    boxShadow: 'none',
    color:'#fff'
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: '#EF8322',
    borderColor: '#EF8322',
  },
  '&:focus': {
    boxShadow: 'none',
  },
});
*/

const chartSet = {
  xAxis: [
    {
      label: '',
    },
  ],
  maxWidth: 450,
  height: 300,
  slotProps: {
    legend: {
      direction: 'row',
      position: { vertical: 'bottom', horizontal: 'middle' },
      padding: -5,
      itemMarkWidth: 10,
      itemMarkHeight: 10,
      labelStyle: {
        fontSize: 12,
      },
    },
  },
};

export default HistoryBurnKcal;